import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    CircularProgress,
    Container,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TablePagination,
    TableHead,
    TableRow,
    Typography,
    Alert,
    Chip,
    Toolbar,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Stack,
} from '@mui/material';
import { Feedback, FeedbackService } from './services/FeedbackService.ts';
import { NavigationRoutes } from '../../application/router/routes.ts';

const feedbackService = new FeedbackService();

const TYPE_SV: Record<string, string> = {
    'General Feedback': 'Allmän feedback',
    'Bug report': 'Felrapport',
    'Feature request': 'Funktionsförslag',
};

const CATEGORY_SV: Record<string, string> = {
    General: 'Allmänt',
    'Barcode scanning': 'Streckkodsskanning',
    'Inventory management': 'Lagerhantering',
    'Dose bag management': 'Dospåshantering',
    Authentication: 'Inloggning',
    'User interface': 'Användargränssnitt',
    Performance: 'Prestanda',
    Other: 'Övrigt',
};

const PRIORITY_SV: Record<string, string> = {
    Low: 'Låg',
    Medium: 'Medium',
    High: 'Hög',
    Critical: 'Kritisk',
};
const STATUS_SV: Record<string, string> = {
    new: 'Ny',
    in_progress: 'Pågående',
    closed: 'Avslutad',
};
const STATUS_ORDER: string[] = ['new', 'in_progress', 'closed'];

// Alias och ordning för konsekvent UI
const TYPE_ALIAS: Record<string, string> = {
    Feature: 'Feature request',
};
const TYPE_ORDER: string[] = [
    'General Feedback',
    'Bug report',
    'Feature request',
];
const CATEGORY_ORDER: string[] = [
    'General',
    'Barcode scanning',
    'Inventory management',
    'Dose bag management',
    'Authentication',
    'User interface',
    'Performance',
    'Other',
];
const PRIORITY_ORDER: string[] = ['Low', 'Medium', 'High', 'Critical'];

const FeedbackList = () => {
    const navigate = useNavigate();
    const [feedbacks, setFeedbacks] = useState<Feedback[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [search, setSearch] = useState('');
    const [filterType, setFilterType] = useState<string>('');
    const [filterCategory, setFilterCategory] = useState<string>('');
    const [filterPriority, setFilterPriority] = useState<string>('');
    const [filterStatus, setFilterStatus] = useState<string>('');

    useEffect(() => {
        loadFeedbacks();
    }, []);

    const loadFeedbacks = async () => {
        try {
            setLoading(true);
            const data = await feedbackService.getAll();
            setFeedbacks(data);
        } catch (err) {
            setError('Kunde inte hämta feedback.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" mt={4}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Container maxWidth="lg" sx={{ mt: 4 }}>
                <Alert severity="error">{error}</Alert>
            </Container>
        );
    }

    const normalized = (s: string) =>
        (s || '')
            .toLowerCase()
            .normalize('NFD')
            .replace(/\p{Diacritic}/gu, '');

    const canonicalType = (t: string) => TYPE_ALIAS[t] ?? t;

    const filtered = feedbacks
        .filter((f) =>
            normalized(
                `${f.title} ${f.description ?? ''} ${f.userName ?? ''} ${f.userEmail ?? ''}`
            ).includes(normalized(search))
        )
        .filter((f) =>
            filterType ? canonicalType(f.type) === filterType : true
        )
        .filter((f) => (filterCategory ? f.category === filterCategory : true))
        .filter((f) => (filterPriority ? f.priority === filterPriority : true))
        .filter((f) => (filterStatus ? f.status === filterStatus : true));

    return (
        <Container maxWidth="lg" sx={{ mt: 4 }}>
            <Box
                sx={{
                    mb: 2,
                    display: 'flex',
                    alignItems: 'baseline',
                    justifyContent: 'space-between',
                }}
            >
                <Typography variant="h4" sx={{ fontWeight: 600 }}>
                    Support / Feedback
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Visar {Math.min((page + 1) * rowsPerPage, filtered.length)}{' '}
                    av {filtered.length}
                </Typography>
            </Box>
            <Paper elevation={3} sx={{ mb: 2 }}>
                <Toolbar
                    sx={{
                        gap: 2,
                        flexWrap: 'wrap',
                        px: 2,
                        py: 1.5,
                    }}
                >
                    <TextField
                        value={search}
                        onChange={(e) => {
                            setSearch(e.target.value);
                            setPage(0);
                        }}
                        size="small"
                        label="Sök"
                        placeholder="Titel, beskrivning eller användare"
                        sx={{
                            minWidth: 240,
                            '& .MuiInputBase-input': { py: 1.1 },
                        }}
                    />
                    <FormControl size="small" sx={{ minWidth: 200 }}>
                        <InputLabel>Typ</InputLabel>
                        <Select
                            label="Typ"
                            value={filterType}
                            onChange={(e) => {
                                setFilterType(e.target.value);
                                setPage(0);
                            }}
                            sx={{
                                '& .MuiSelect-select': { py: 1.1 },
                            }}
                        >
                            <MenuItem value="">
                                <em>Alla</em>
                            </MenuItem>
                            {TYPE_ORDER.map((t) => (
                                <MenuItem key={t} value={t}>
                                    {TYPE_SV[t]}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 220 }}>
                        <InputLabel>Kategori</InputLabel>
                        <Select
                            label="Kategori"
                            value={filterCategory}
                            onChange={(e) => {
                                setFilterCategory(e.target.value);
                                setPage(0);
                            }}
                            sx={{
                                '& .MuiSelect-select': { py: 1.1 },
                            }}
                        >
                            <MenuItem value="">
                                <em>Alla</em>
                            </MenuItem>
                            {CATEGORY_ORDER.map((c) => (
                                <MenuItem key={c} value={c}>
                                    {CATEGORY_SV[c] ?? c}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 180 }}>
                        <InputLabel>Prioritet</InputLabel>
                        <Select
                            label="Prioritet"
                            value={filterPriority}
                            onChange={(e) => {
                                setFilterPriority(e.target.value);
                                setPage(0);
                            }}
                            sx={{
                                '& .MuiSelect-select': { py: 1.1 },
                            }}
                        >
                            <MenuItem value="">
                                <em>Alla</em>
                            </MenuItem>
                            {PRIORITY_ORDER.map((p) => (
                                <MenuItem key={p} value={p}>
                                    {PRIORITY_SV[p] ?? p}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                        <InputLabel>Status</InputLabel>
                        <Select
                            label="Status"
                            value={filterStatus}
                            onChange={(e) => {
                                setFilterStatus(e.target.value);
                                setPage(0);
                            }}
                            sx={{
                                '& .MuiSelect-select': { py: 1.1 },
                            }}
                        >
                            <MenuItem value="">
                                <em>Alla</em>
                            </MenuItem>
                            {STATUS_ORDER.map((s) => (
                                <MenuItem key={s} value={s}>
                                    {STATUS_SV[s] ?? s}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Stack
                        direction="row"
                        spacing={1}
                        sx={{
                            mt: 1,
                            width: '100%',
                            '& .MuiChip-root': { my: 0.25 },
                        }}
                    >
                        {filterType && (
                            <Chip
                                size="small"
                                label={`Typ: ${TYPE_SV[filterType] ?? filterType}`}
                                onDelete={() => setFilterType('')}
                                color="default"
                            />
                        )}
                        {filterCategory && (
                            <Chip
                                size="small"
                                label={`Kategori: ${CATEGORY_SV[filterCategory] ?? filterCategory}`}
                                onDelete={() => setFilterCategory('')}
                                color="default"
                            />
                        )}
                        {filterPriority && (
                            <Chip
                                size="small"
                                label={`Prioritet: ${PRIORITY_SV[filterPriority] ?? filterPriority}`}
                                onDelete={() => setFilterPriority('')}
                                color="default"
                            />
                        )}
                        {filterStatus && (
                            <Chip
                                size="small"
                                label={`Status: ${STATUS_SV[filterStatus] ?? filterStatus}`}
                                onDelete={() => setFilterStatus('')}
                                color="default"
                            />
                        )}
                    </Stack>
                </Toolbar>
            </Paper>
            <Paper elevation={3}>
                <TableContainer>
                    <Table stickyHeader size="medium">
                        <TableHead>
                            <TableRow>
                                <TableCell>Typ</TableCell>
                                <TableCell>Kategori</TableCell>
                                <TableCell>Prioritet</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Titel</TableCell>
                                <TableCell>Användare</TableCell>
                                <TableCell>Datum</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filtered
                                .slice()
                                .sort((a, b) => {
                                    const ta = a.timestamp
                                        ? new Date(a.timestamp).getTime()
                                        : 0;
                                    const tb = b.timestamp
                                        ? new Date(b.timestamp).getTime()
                                        : 0;
                                    return tb - ta;
                                })
                                .slice(
                                    page * rowsPerPage,
                                    page * rowsPerPage + rowsPerPage
                                )
                                .map((row) => (
                                    <TableRow
                                        key={row.id}
                                        hover
                                        onClick={() =>
                                            navigate(
                                                NavigationRoutes.feedbackDetail.replace(
                                                    ':id',
                                                    row.id
                                                )
                                            )
                                        }
                                        sx={{ cursor: 'pointer' }}
                                    >
                                        <TableCell>
                                            <Chip
                                                label={
                                                    TYPE_SV[row.type] ??
                                                    row.type
                                                }
                                                size="small"
                                                variant="outlined"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            {CATEGORY_SV[row.category] ??
                                                row.category}
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={
                                                    PRIORITY_SV[row.priority] ??
                                                    row.priority
                                                }
                                                size="small"
                                                color={
                                                    row.priority === 'Critical'
                                                        ? 'error'
                                                        : row.priority ===
                                                            'High'
                                                          ? 'warning'
                                                          : row.priority ===
                                                              'Medium'
                                                            ? 'info'
                                                            : 'default'
                                                }
                                                variant="filled"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={
                                                    STATUS_SV[row.status] ??
                                                    row.status
                                                }
                                                size="small"
                                                color={
                                                    row.status === 'closed'
                                                        ? 'success'
                                                        : row.status ===
                                                            'in_progress'
                                                          ? 'info'
                                                          : 'default'
                                                }
                                                variant="outlined"
                                            />
                                        </TableCell>
                                        <TableCell>{row.title}</TableCell>
                                        <TableCell>
                                            {row.userName || row.userEmail}
                                        </TableCell>
                                        <TableCell>
                                            {row.timestamp
                                                ? new Date(
                                                      row.timestamp
                                                  ).toLocaleString('sv-SE')
                                                : '-'}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            {filtered.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={6} align="center">
                                        Inga resultat
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    component="div"
                    count={filtered.length}
                    page={page}
                    onPageChange={(_, newPage) => setPage(newPage)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => {
                        setRowsPerPage(parseInt(e.target.value, 10));
                        setPage(0);
                    }}
                    rowsPerPageOptions={[5, 10, 25, 50, 100]}
                    labelRowsPerPage="Rader per sida:"
                    labelDisplayedRows={({ from, to, count }) =>
                        `${from}–${to} av ${count !== -1 ? count : `fler än ${to}`}`
                    }
                />
            </Paper>
        </Container>
    );
};

export default FeedbackList;
